"use client";

import { useEffect, useRef, type UIEvent } from "react";
import { Image } from "@/components/ui/image";
import type { GalleryPhoto } from "./listing-gallery";
import { LISTING_GALLERY_SIZES } from "@/components/features/listings/image-sizes";

function slideAlt(item: GalleryPhoto, index: number, productTitle?: string) {
  if (item.caption) return item.caption;
  const position = index + 1;
  return productTitle
    ? `${productTitle} — photo ${position}`
    : `Product image ${position}`;
}

function useSnapScrollSync(
  activeIndex: number,
  onActiveChange: (index: number) => void,
) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = ref.current;
    if (!scroller || scroller.clientWidth === 0) return;
    const current = Math.round(scroller.scrollLeft / scroller.clientWidth);
    if (current !== activeIndex) {
      scroller.scrollTo({ left: activeIndex * scroller.clientWidth });
    }
  }, [activeIndex]);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (target.clientWidth === 0) return;
    const next = Math.round(target.scrollLeft / target.clientWidth);
    if (next !== activeIndex) onActiveChange(next);
  };

  return { ref, onScroll };
}

export function ListingGalleryPhone({
  media,
  activeIndex,
  productTitle,
  onActiveChange,
  onEnlarge,
}: {
  media: GalleryPhoto[];
  activeIndex: number;
  productTitle?: string;
  onActiveChange: (index: number) => void;
  onEnlarge: (index: number) => void;
}) {
  const { ref, onScroll } = useSnapScrollSync(activeIndex, onActiveChange);

  return (
    <div
      data-slot="listing-gallery-phone"
      className="relative -mx-4 md:hidden"
    >
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {media.map((item, index) => (
          <PhoneGallerySlide
            key={item._id}
            item={item}
            index={index}
            productTitle={productTitle}
            onEnlarge={onEnlarge}
          />
        ))}
      </div>
      {media.length > 1 ? (
        <span className="pointer-events-none absolute bottom-3 right-4 rounded-full bg-foreground/70 px-2.5 py-0.5 tabular-nums text-xs text-background">
          {activeIndex + 1} / {media.length}
        </span>
      ) : null}
    </div>
  );
}

function PhoneGallerySlide({
  item,
  index,
  productTitle,
  onEnlarge,
}: {
  item: GalleryPhoto;
  index: number;
  productTitle?: string;
  onEnlarge: (index: number) => void;
}) {
  return (
    <figure className="relative aspect-[4/3] w-full shrink-0 snap-center overflow-hidden bg-surface-2">
      <Image
        src={item.url}
        alt=""
        aria-hidden="true"
        fill
        className="scale-110 object-cover opacity-60 blur-2xl"
        sizes={LISTING_GALLERY_SIZES}
        loading={index === 0 ? "eager" : undefined}
      />
      <Image
        src={item.url}
        alt={slideAlt(item, index, productTitle)}
        fill
        className="object-contain"
        sizes={LISTING_GALLERY_SIZES}
        loading={index === 0 ? "eager" : undefined}
        fetchPriority={index === 0 ? "high" : undefined}
      />
      <button
        type="button"
        onClick={() => onEnlarge(index)}
        aria-label="Enlarge photo"
        className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/20"
      />
    </figure>
  );
}
